import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Space, Tag, Modal, Form, Input, Select, InputNumber,
  Typography, Row, Col, Statistic, Popconfirm, Tooltip, message, Alert,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  IdcardOutlined, EyeOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { jobsApi, Job, JobCreatePayload, JobStats } from '../../api/jobs';

const { Title, Text } = Typography;
const { TextArea } = Input;

const CATEGORY_CONFIG: Record<string, { label: string; color: string }> = {
  office: { label: 'Офис', color: 'blue' },
  service: { label: 'Обслуживание', color: 'green' },
  construction: { label: 'Строительство', color: 'orange' },
  education: { label: 'Образование', color: 'purple' },
  medical: { label: 'Медицина', color: 'red' },
  government: { label: 'Госслужба', color: 'cyan' },
  seasonal: { label: 'Сезонная', color: 'gold' },
  other: { label: 'Другое', color: 'default' },
};

const EMPLOYMENT_CONFIG: Record<string, { label: string; color: string }> = {
  full: { label: 'Полная', color: 'blue' },
  part: { label: 'Частичная', color: 'green' },
  temporary: { label: 'Временная', color: 'orange' },
  remote: { label: 'Удалённая', color: 'purple' },
};

const JobsList: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<JobStats | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Job | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const [messageApi, contextHolder] = message.useMessage();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [jobsRes, statsRes] = await Promise.allSettled([
        jobsApi.list(),
        jobsApi.stats(),
      ]);
      if (jobsRes.status === 'fulfilled') setJobs(jobsRes.value.data);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ category: 'other', employment_type: 'full' });
    setModalOpen(true);
  };

  const openEdit = (record: Job) => {
    setEditing(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    let values: any;
    try { values = await form.validateFields(); } catch { return; }

    setSubmitting(true);
    try {
      const payload: JobCreatePayload = {
        title: values.title,
        description: values.description,
        company: values.company,
        salary_from: values.salary_from,
        salary_to: values.salary_to,
        category: values.category,
        employment_type: values.employment_type,
        contact_phone: values.contact_phone,
        contact_name: values.contact_name,
        address: values.address,
      };

      if (editing) {
        await jobsApi.update(editing.id, payload);
        messageApi.success('Вакансия обновлена');
      } else {
        await jobsApi.create(payload);
        messageApi.success('Вакансия создана');
      }
      setModalOpen(false);
      fetchData();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await jobsApi.delete(id);
      messageApi.success('Вакансия удалена');
      fetchData();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка удаления');
    }
  };

  const columns: ColumnsType<Job> = [
    {
      title: 'Название',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (title: string, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{title}</Text>
          {r.company && <Text type="secondary" style={{ fontSize: 12 }}>{r.company}</Text>}
        </Space>
      ),
    },
    {
      title: 'Зарплата',
      key: 'salary',
      width: 160,
      render: (_: any, r: Job) => {
        if (!r.salary_from && !r.salary_to) return <Text type="secondary">Договорная</Text>;
        const from = r.salary_from ? `${r.salary_from.toLocaleString('ru-RU')}` : '';
        const to = r.salary_to ? `${r.salary_to.toLocaleString('ru-RU')}` : '';
        if (from && to) return <Text strong>{from} — {to} тг</Text>;
        return <Text strong>от {from || to} тг</Text>;
      },
    },
    {
      title: 'Категория',
      dataIndex: 'category',
      key: 'category',
      width: 130,
      render: (cat: string) => {
        const cfg = CATEGORY_CONFIG[cat] || { label: cat, color: 'default' };
        return <Tag>{cfg.label}</Tag>;
      },
    },
    {
      title: 'Занятость',
      dataIndex: 'employment_type',
      key: 'employment_type',
      width: 120,
      render: (type: string) => {
        const cfg = EMPLOYMENT_CONFIG[type] || { label: type, color: 'default' };
        return <Tag>{cfg.label}</Tag>;
      },
    },
    {
      title: 'Просмотры',
      dataIndex: 'views_count',
      key: 'views_count',
      width: 100,
      align: 'center',
      render: (v: number) => <Space><EyeOutlined />{v}</Space>,
    },
    {
      title: 'Статус',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (active: boolean) =>
        active ? <Tag color="green">Активна</Tag> : <Tag color="red">Закрыта</Tag>,
    },
    {
      title: 'Дата',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 110,
      render: (d: string) => dayjs(d).format('DD.MM.YYYY'),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 100,
      render: (_: any, record: Job) => (
        <Space size={4}>
          <Tooltip title="Редактировать">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Popconfirm title="Удалить вакансию?" onConfirm={() => handleDelete(record.id)} okText="Да" cancelText="Нет">
            <Tooltip title="Удалить">
              <Button size="small" icon={<DeleteOutlined />} danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {contextHolder}

      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={3} style={{ margin: 0, color: 'var(--l-ink)' }}>Вакансии</Title>
          <Text type="secondary">Рынок труда города Аксай и Бурлинского района</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} style={{ background: 'var(--l-accent)' }}>
          Добавить вакансию
        </Button>
      </div>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card><Statistic title="Всего вакансий" value={stats?.total ?? jobs.length} prefix={<IdcardOutlined />} /></Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ }}><Statistic title="Активных" value={stats?.active ?? 0} valueStyle={{ color: 'var(--l-ok)' }} /></Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ }}><Statistic title="Закрытых" value={(stats?.total ?? 0) - (stats?.active ?? 0)} valueStyle={{ color: 'var(--l-accent)' }} /></Card>
        </Col>
      </Row>

      {error && <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}

      <Card bodyStyle={{ padding: 0 }} extra={<Button icon={<ReloadOutlined />} onClick={fetchData}>Обновить</Button>}>
        <Table<Job> rowKey="id" columns={columns} dataSource={jobs} loading={loading} pagination={{ pageSize: 20, showTotal: t => `Всего: ${t}` }} locale={{ emptyText: 'Вакансии не найдены' }} scroll={{ x: 1000 }} />
      </Card>

      {/* Create / Edit Modal */}
      <Modal title={editing ? 'Редактировать вакансию' : 'Добавить вакансию'} open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleSubmit} confirmLoading={submitting} okText={editing ? 'Сохранить' : 'Создать'} cancelText="Отмена" width={700} destroyOnClose>
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="title" label="Название вакансии" rules={[{ required: true, message: 'Введите название' }]}>
            <Input placeholder="Бухгалтер, Водитель, Продавец..." />
          </Form.Item>
          <Form.Item name="description" label="Описание">
            <TextArea rows={3} placeholder="Требования и условия работы" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="company" label="Компания">
                <Input placeholder="ТОО «Компания»" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="address" label="Адрес">
                <Input placeholder="г. Аксай, ул. ..." />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="salary_from" label="Зарплата от (тг)">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="salary_to" label="Зарплата до (тг)">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="category" label="Категория" rules={[{ required: true }]}>
                <Select>
                  {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                    <Select.Option key={key} value={key}>{cfg.label}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="employment_type" label="Тип занятости" rules={[{ required: true }]}>
                <Select>
                  {Object.entries(EMPLOYMENT_CONFIG).map(([key, cfg]) => (
                    <Select.Option key={key} value={key}>{cfg.label}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="contact_name" label="Контактное лицо">
                <Input placeholder="Иванов И.И." />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="contact_phone" label="Телефон">
                <Input placeholder="+7 7XX XXX XX XX" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default JobsList;
